<script lang="ts" setup>
import AddressDataService from '@/services/AddressDataService'
import type Address from '@/types/Address'
import type ResponseData from '@/types/ResponseData'
import type FakturacneUdaje from '@/types/FakturacneUdaje'
import UserDataService from '@/services/UserDataService'
import type User from '@/types/User'
import HeadquartersDataService from '@/services/HeadquartersDataService'
import type Headquarters from '@/types/Headquarters'
import CompanyDataService from '@/services/CompanyDataService'
import type Company from '@/types/Company'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import CompanyMemberDataService from '@/services/CompanyMemberDataService'
import type CompanyMember from '@/types/CompanyMember'
import { onMounted, ref, computed } from 'vue';
import store from '@/store'
import OrderDataService from '@/services/OrderDataService'
import {
    MinusIcon,
    PlusIcon
} from '@heroicons/vue/outline'

const currentOrder = computed(() => store.state.currentOrder);

const currentOrderAddress = ref({} as Address);
const currentCompanyAddress = ref({} as Address);
const currentUserAddress = ref({} as Address);
const currentFakturacneUdaje = ref({} as FakturacneUdaje);
const currentUser = ref({} as User);
const currentCompany = ref({} as Company);
const currentHeadquarters = ref({} as Headquarters);
const currentCompanyMembers = ref([] as CompanyMember[]);
const items = ref([] as any[]);
const tab = ref(1);
const companySubStatuses = ref([] as any);
const currentCompanyUpdateOrder = ref({} as any);
const sharesTransfers = ref([] as any[]);
const actual_company = ref({} as any);
const updated_company = ref({} as any);
const headquartersTypes = ref([] as any);

function currentTab(tabNumber: number) {
    tab.value = tabNumber
}

async function getHeadquartersTypes() {
    HeadquartersDataService.getHeadquartersTypes()
        .then((response: ResponseData) => {
            headquartersTypes.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUser(id: any) {
    await UserDataService.getUserById(id)
        .then(async (response: ResponseData) => {
            currentUser.value = response.data.data
            if (currentUser.value.address_id > 0)
                await getUserAddress(currentUser.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompany(id: any) {
    await CompanyDataService.getCompanyById(id)
        .then((response: ResponseData) => {
            currentCompany.value = response.data.data
            getCompanyHeadquarters(currentCompany.value.headquarters_id)
            getCompanyMembers(currentCompany.value.id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyMembers(id: any) {
    await CompanyMemberDataService.getAllCompanyMembersForCompany(id)
        .then((response: ResponseData) => {
            currentCompanyMembers.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getOrderAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentOrderAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getUserAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentUserAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyAddress(id: any) {
    await AddressDataService.getAddressById(id)
        .then((response: ResponseData) => {
            currentCompanyAddress.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanyHeadquarters(id: any) {
    await HeadquartersDataService.getHeadquartersById(id)
        .then(async (response: ResponseData) => {
            currentHeadquarters.value = response.data.data
            if (currentHeadquarters.value.address_id > 0)
                await getCompanyAddress(currentHeadquarters.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getFakturacneUdaje(id: any) {
    await UserDataService.getFakturacneUdajeById(id)
        .then(async (response: ResponseData) => {
            currentFakturacneUdaje.value = response.data.data
            if (currentFakturacneUdaje.value.address_id > 0)
                await getOrderAddress(currentFakturacneUdaje.value.address_id)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function getCompanySubStatuses() {
    CompanyDataService.getCompanySubStatuses()
        .then((response: ResponseData) => {
            companySubStatuses.value = response.data.data
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function updateSubStatus() {
    await CompanyDataService.update(currentCompany.value.id, currentCompany.value)
        .then(() => {
            toast.success('Stav žiadosti bol úspešne zmenený');
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message);
        });
}

async function getCompanyUpdateOrder() {
    OrderDataService.getCompanyUpdateOrder(currentOrder.value.id)
        .then((response: ResponseData) => {
            currentCompanyUpdateOrder.value = response.data.data
            const parsedTransfers = JSON.parse(currentCompanyUpdateOrder.value.sharesTransfers)
            // Handle both old flat format (SharesTransfers[]) and new nested format (SharesTransfers[][])
            sharesTransfers.value = Array.isArray(parsedTransfers[0]) ? parsedTransfers.flat() : parsedTransfers
            actual_company.value = JSON.parse(currentCompanyUpdateOrder.value.actual_company)
            updated_company.value = JSON.parse(currentCompanyUpdateOrder.value.updated_company)
        })
        .catch((e: Error) => {
            toast.error('Chyba: ' + e.message)
        })
}

async function refreshData() {
    items.value = JSON.parse(currentOrder.value.items)

    await getFakturacneUdaje(currentOrder.value.fakturacne_udaje_id)
    await getUser(currentOrder.value.user_id)
    if (currentOrder.value.company_id !== null) {
        await getCompany(currentOrder.value.company_id)
    }
    await getCompanySubStatuses();

    if (currentOrder.value.order_type == "cupdate") {
        await getCompanyUpdateOrder();
    }

    await getHeadquartersTypes();
}

onMounted(async () => {
    await refreshData();
})
</script>

<template>
    <div class="max-w-4xl mx-auto">
        <!-- Header Card -->
        <div class="bg-white rounded-xl shadow p-6 border border-gray-100 mb-6 flex flex-col gap-4">
            <div class="flex flex-row items-center gap-3">
                <svg class="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Zmena firmy">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8M8 16h8M8 8h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                <span class="text-2xl font-bold text-slate-900">Zmena firmy (s.r.o.)</span>
                <span v-if="items.length && items[0].price" class="ml-2 text-xl font-semibold text-teal-600"
                    title="Celková cena">- {{ items[0].price }}€</span>
            </div>
            <div class="flex items-center gap-2 text-lg font-semibold text-slate-700">
                <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24" title="Názov firmy">
                    <path d="M16 7a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" />
                </svg>
                {{ currentCompany.name }}
            </div>
            <div class="flex flex-col gap-2 mt-2">
                <label for="sub_status" class="block text-sm font-medium text-gray-700">Stav žiadosti</label>
                <select v-model="currentCompany.sub_status" name="sub_status" id="sub_status"
                    @change="updateSubStatus()"
                    class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                    <option v-for="status in companySubStatuses" :value="status.id"
                        :selected="status.id == currentCompany.sub_status">{{ status.description }}</option>
                </select>
            </div>
        </div>
        <!-- Tabs -->
        <div class="flex flex-row justify-center gap-2 mb-6">
            <button @click="currentTab(1)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 1 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Faktúrované služby">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                        fill="none" />
                    <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Faktúrované služby
            </button>
            <button @click="currentTab(2)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 2 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Zmeny v spoločnosti">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
                Zmeny v spoločnosti
            </button>
            <button @click="currentTab(3)" type="button"
                :class="['inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition', tab === 3 ? 'bg-teal-600 text-white shadow' : 'bg-slate-100 text-slate-700 hover:bg-teal-50 border border-gray-200']"
                title="Prevod akcií">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                    <path d="M16 17l-4 4m0 0l-4-4m4 4V3" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" />
                </svg>
                Prevod akcií
            </button>
        </div>
        <div class="bg-white rounded-xl shadow p-6 border border-gray-100">
            <!-- Faktúrované služby -->
            <div v-if="tab === 1">
                <div class="mb-4 flex items-center gap-3 p-4 rounded-lg bg-slate-50 border border-slate-100">
                    <svg class="w-6 h-6 text-teal-500" fill="none" stroke="currentColor" stroke-width="2"
                        viewBox="0 0 24 24" title="Súčet cien">
                        <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" stroke-width="2"
                            fill="none" />
                        <path d="M8 12h8" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                    </svg>
                    <span class="font-semibold text-slate-900">Suma všetkých služieb:</span>
                    <span class="ml-auto text-xl font-bold text-teal-700">{{items.reduce((sum, i) => sum +
                        (isNaN(parseFloat(i.price ?? '0')) ? 0 : parseFloat(i.price ?? '0')), 0).toFixed(2)}} €</span>
                </div>
                <div class="shadow overflow-hidden sm:rounded-md">
                    <div class="px-4 py-5 bg-white sm:p-6 flex flex-col gap-4">
                        <div v-for="(item, index) in items" :key="index"
                            class="transition hover:bg-teal-50 rounded-lg p-3 group border border-slate-100">
                            <div class="grid grid-cols-9 gap-6 items-center">
                                <div class="col-span-1 text-center self-center text-slate-400 font-semibold">#{{ index +
                                    1
                                }}</div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="description"
                                        class="block text-sm font-medium text-gray-700">Popis</label>
                                    <div>{{ item.description }}</div>
                                </div>
                                <div class="col-span-3 sm:col-span-3">
                                    <label for="amount" class="block text-sm font-medium text-gray-700">Suma</label>
                                    <FormKit type="text" v-model="item.price" name="amount" id="amount"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="col-span-2 sm:col-span-2">
                                    <label for="amount" class="block text-sm font-medium text-gray-700">DPH</label>
                                    <FormKit type="text" v-model="item.price_vat" name="amount" id="amount"
                                        class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!--Súčasná spoločnosť-->
            <div v-if="tab === 2">
                <!-- Legend -->
                <div class="flex flex-wrap gap-3 mb-6 p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm">
                    <span class="inline-flex items-center gap-1.5 font-medium text-slate-600">
                        <span class="w-3 h-3 rounded-full bg-slate-400 inline-block"></span>
                        Aktuálny stav
                    </span>
                    <span class="inline-flex items-center gap-1.5 font-medium text-teal-700">
                        <span class="w-3 h-3 rounded-full bg-teal-500 inline-block"></span>
                        Nový stav (zmena)
                    </span>
                    <span class="inline-flex items-center gap-1.5 font-medium text-green-700">
                        <span class="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
                        Vyvolanie konateľa
                    </span>
                    <span class="inline-flex items-center gap-1.5 font-medium text-red-700">
                        <span class="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
                        Odvolanie konateľa
                    </span>
                </div>

                <div class="flex flex-col gap-6">
                    <!-- Základné údaje -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálna -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Aktuálna spoločnosť</span>
                            </div>
                            <div class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Meno spoločnosti</label>
                                    <FormKit type="text" v-model="actual_company.obchodne_meno" id="act_obchodne_meno"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">IČO</label>
                                    <FormKit type="text" v-model="actual_company.ico" id="act_ico"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Právna forma</label>
                                    <FormKit type="text" v-model="actual_company.pravna_forma" id="act_pravna_forma"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Konanie menom spoločnosti</label>
                                    <FormKit type="textarea" v-model="actual_company.konanie_menom_spolocnosti" id="act_konanie"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col sm:col-span-2" v-if="actual_company.zakladne_imanie">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Základné imanie</label>
                                    <FormKit type="text" v-model="actual_company.zakladne_imanie" id="act_zakladne_imanie"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                        <!-- Zmenená -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Zmenená spoločnosť</span>
                            </div>
                            <div class="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-teal-700 mb-1">Meno spoločnosti</label>
                                    <FormKit type="text" v-model="updated_company.obchodne_meno" id="upd_obchodne_meno"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-teal-700 mb-1">IČO</label>
                                    <FormKit type="text" v-model="updated_company.ico" id="upd_ico"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-teal-700 mb-1">Právna forma</label>
                                    <FormKit type="text" v-model="updated_company.pravna_forma" id="upd_pravna_forma"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col sm:col-span-2">
                                    <label class="block text-xs font-medium text-teal-700 mb-1">Konanie menom spoločnosti</label>
                                    <FormKit type="textarea" v-model="updated_company.konanie_menom_spolocnosti" id="upd_konanie"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <!-- Základné imanie changes -->
                                <div class="flex flex-col sm:col-span-2" v-if="updated_company.zakladne_imanie?.length > 0">
                                    <label class="block text-xs font-semibold text-teal-700 mb-2">Základné imanie — zmeny</label>
                                    <div v-for="(imanie, index) in updated_company.zakladne_imanie" :key="index">
                                        <div v-if="imanie.change_for" class="flex flex-wrap items-center gap-2 mb-2 p-2 rounded bg-white border border-teal-100">
                                            <component :is="imanie.operation == '+' ? PlusIcon : MinusIcon"
                                                :class="imanie.operation == '+' ? 'h-5 w-5 text-green-500' : 'h-5 w-5 text-red-500'" />
                                            <div class="flex flex-col flex-1 min-w-0">
                                                <label class="block text-xs font-medium text-gray-600 mb-1">Zmena pre</label>
                                                <FormKit type="text" v-model="imanie.change_for"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col flex-1 min-w-0">
                                                <label class="block text-xs font-medium text-gray-600 mb-1">Hodnota</label>
                                                <FormKit type="text" v-model="imanie.value"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Sídlo -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálne sídlo -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Sídlo — aktuálne</span>
                            </div>
                            <div class="p-4 grid grid-cols-2 gap-3">
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Platné od</label>
                                    <FormKit type="text" v-model="actual_company.sidlo.since" id="act_since"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">PSČ</label>
                                    <FormKit type="text" v-model="actual_company.sidlo.zip" id="act_zip"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Mesto</label>
                                    <FormKit type="text" v-model="actual_company.sidlo.city" id="act_city"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Ulica</label>
                                    <FormKit type="text" v-model="actual_company.sidlo.street" id="act_street"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                                <div class="flex flex-col">
                                    <label class="block text-xs font-medium text-gray-600 mb-1">Číslo</label>
                                    <FormKit type="text" v-model="actual_company.sidlo.number" id="act_number"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                        <!-- Nové sídlo -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Sídlo — nové</span>
                            </div>
                            <div class="p-4 grid grid-cols-2 gap-3">
                                <template v-if="updated_company.sidlo.address != undefined">
                                    <div class="flex flex-col col-span-2">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Názov sídla</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.name" id="upd_sidlo_name"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">PSČ</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.address.psc" id="upd_zip"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Mesto</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.address.city" id="upd_city"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Ulica</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.address.street" id="upd_street"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Číslo</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.address.street_number" id="upd_number"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Číslo 2</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.address.street_number2" id="upd_number2"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">PSČ</label>
                                        <FormKit type="text" :v-model="updated_company.sidlo.zip ?? updated_company.sidlo.psc" id="upd_zip2"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Mesto</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.city" id="upd_city2"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Ulica</label>
                                        <FormKit type="text" v-model="updated_company.sidlo.street" id="upd_street2"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div class="flex flex-col">
                                        <label class="block text-xs font-medium text-teal-700 mb-1">Číslo</label>
                                        <FormKit type="text" :v-model="updated_company.sidlo.number ?? updated_company.sidlo.street_number" id="upd_number3"
                                            class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <template v-if="updated_company.sidlo?.headquarters_type">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Typ nehnuteľnosti</label>
                                            <FormKit type="select" v-model="updated_company.sidlo.headquarters_type" name="druh_priestoru"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md">
                                                <option class="text-gray-500">Vyberte druh priestoru</option>
                                                <option v-for="hqType in headquartersTypes" :value="hqType.id"
                                                    :selected="hqType.id == updated_company.sidlo.headquarters_type">{{ hqType.description }}</option>
                                            </FormKit>
                                        </div>
                                        <div class="flex flex-col" v-if="updated_company.sidlo?.owner_name">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Majiteľ nehnuteľnosti</label>
                                            <FormKit type="text" v-model="updated_company.sidlo.owner_name" id="upd_owner"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                        </div>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Konatelia -->
                    <div v-if="actual_company.konatelia?.length > 0 || updated_company.konatelia?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálni konatelia -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Konatelia — aktuálni</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <div v-for="(konatel, index) in actual_company.konatelia" :key="'act-kon-' + index"
                                    class="border border-slate-200 rounded-lg p-3 bg-white">
                                    <p class="text-xs font-semibold text-slate-500 mb-2">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Meno</label>
                                            <FormKit type="text" v-model="konatel.name"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Aktívny od</label>
                                            <FormKit type="text" v-model="konatel.since"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col" v-if="konatel.new_konatel_date_from">
                                            <label class="block text-xs font-medium text-green-600 mb-1">Dátum vyvolania</label>
                                            <FormKit type="text" v-model="konatel.new_konatel_date_from"
                                                class="focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-xs border-green-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col" v-if="konatel.cancel_from_date">
                                            <label class="block text-xs font-medium text-red-600 mb-1">Dátum odvolania</label>
                                            <FormKit type="text" v-model="konatel.cancel_from_date"
                                                class="focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-xs border-red-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">PSČ</label>
                                            <FormKit type="text" v-model="konatel.zip"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Mesto</label>
                                            <FormKit type="text" v-model="konatel.city"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Ulica</label>
                                            <FormKit type="text" v-model="konatel.street"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Číslo</label>
                                            <FormKit type="text" v-model="konatel.number"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Noví/zmenení konatelia -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Konatelia — zmeny</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <template v-for="(konatel, index) in updated_company.konatelia" :key="'upd-kon-' + index">
                                    <div v-if="konatel != null"
                                        class="border rounded-lg p-3 bg-white"
                                        :class="konatel.new_konatel_date_from ? 'border-green-400' : konatel.cancel_from_date ? 'border-red-400' : 'border-teal-200'">
                                        <div class="flex items-center justify-between mb-2">
                                            <p class="text-xs font-semibold text-slate-500">#{{ index }}</p>
                                            <span v-if="konatel.new_konatel_date_from"
                                                class="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">Vyvolanie</span>
                                            <span v-else-if="konatel.cancel_from_date"
                                                class="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold">Odvolanie</span>
                                        </div>
                                        <div class="grid grid-cols-2 gap-2">
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Meno</label>
                                                <FormKit type="text" v-model="konatel.first_name"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Priezvisko</label>
                                                <FormKit type="text" v-model="konatel.last_name"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col" v-if="konatel.new_konatel_date_from">
                                                <label class="block text-xs font-medium text-green-700 mb-1">Dátum vyvolania</label>
                                                <FormKit type="text" v-model="konatel.new_konatel_date_from"
                                                    class="focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-xs border-green-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col" v-if="konatel.cancel_from_date">
                                                <label class="block text-xs font-medium text-red-700 mb-1">Dátum odvolania</label>
                                                <FormKit type="text" v-model="konatel.cancel_from_date"
                                                    class="focus:ring-red-500 focus:border-red-500 block w-full shadow-sm sm:text-xs border-red-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Pohlavie</label>
                                                <FormKit type="text" v-model="konatel.gender"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Rodné číslo</label>
                                                <FormKit type="text" v-model="konatel.rodne_cislo"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Dátum narodenia</label>
                                                <FormKit type="text" v-model="konatel.date_of_birth"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Titul pred menom</label>
                                                <FormKit type="text" v-model="konatel.title_before"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Titul za menom</label>
                                                <FormKit type="text" v-model="konatel.title_after"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">PSČ</label>
                                                <FormKit type="text" v-model="konatel.psc"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Mesto</label>
                                                <FormKit type="text" v-model="konatel.city"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Ulica</label>
                                                <FormKit type="text" v-model="konatel.street"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Číslo</label>
                                                <FormKit type="text" v-model="konatel.street_number"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Krajina</label>
                                                <FormKit type="text" v-model="konatel.country"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-sm text-slate-400 italic px-3 py-2 bg-white border border-slate-100 rounded-lg">#{{ index }} — Bez zmien</div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Spoločníci -->
                    <div v-if="actual_company.spolocnici?.length > 0 || (Array.isArray(updated_company.spolocnici) && updated_company.spolocnici.length > 0)"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálni spoločníci -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Spoločníci — aktuálni</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <div v-for="(spolocnik, index) in actual_company.spolocnici" :key="'act-spol-' + index"
                                    class="border border-slate-200 rounded-lg p-3 bg-white">
                                    <p class="text-xs font-semibold text-slate-500 mb-2">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Meno</label>
                                            <FormKit type="text" v-model="spolocnik.name"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Obchodné meno</label>
                                            <FormKit type="text" v-model="spolocnik.obchodne_meno"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">IČO</label>
                                            <FormKit type="text" v-model="spolocnik.ico"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Aktívny od</label>
                                            <FormKit type="text" v-model="spolocnik.since"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Podiel v spoločnosti</label>
                                            <FormKit type="text" v-model="spolocnik.podiel_v_spolocnosti"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Výška vkladu</label>
                                            <FormKit type="text" v-model="spolocnik.vyska_vkladu"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Rozsah splatenia</label>
                                            <FormKit type="text" v-model="spolocnik.rozsah_splatenia_vkladu"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">PSČ</label>
                                            <FormKit type="text" v-model="spolocnik.zip"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Mesto</label>
                                            <FormKit type="text" v-model="spolocnik.city"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Ulica</label>
                                            <FormKit type="text" v-model="spolocnik.street"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-gray-600 mb-1">Číslo</label>
                                            <FormKit type="text" v-model="spolocnik.number"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Noví/zmenení spoločníci -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Spoločníci — zmeny</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <template v-for="(spolocnik, index) in updated_company.spolocnici" :key="'upd-spol-' + index">
                                    <div v-if="spolocnik && typeof spolocnik === 'object' && spolocnik.first_name != null"
                                        class="border border-teal-200 rounded-lg p-3 bg-white">
                                        <p class="text-xs font-semibold text-slate-500 mb-2">#{{ index }}</p>
                                        <div class="grid grid-cols-2 gap-2">
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Meno</label>
                                                <FormKit type="text" v-model="spolocnik.first_name"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Priezvisko</label>
                                                <FormKit type="text" v-model="spolocnik.last_name"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Aktívny od</label>
                                                <FormKit type="text" v-model="spolocnik.since"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Podiel v spoločnosti</label>
                                                <FormKit type="text" v-model="spolocnik.podiel_v_spolocnosti"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Výška vkladu</label>
                                                <FormKit type="text" v-model="spolocnik.vyska_vkladu"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Rozsah splatenia</label>
                                                <FormKit type="text" v-model="spolocnik.rozsah_splatenia_vkladu"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">PSČ</label>
                                                <FormKit type="text" v-model="spolocnik.psc"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Mesto</label>
                                                <FormKit type="text" v-model="spolocnik.city"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Ulica</label>
                                                <FormKit type="text" v-model="spolocnik.street"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                            <div class="flex flex-col">
                                                <label class="block text-xs font-medium text-teal-700 mb-1">Číslo</label>
                                                <FormKit type="text" v-model="spolocnik.street_number"
                                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="text-gray-400 italic text-xs py-2 px-3 bg-white border border-slate-100 rounded-lg">Neplatný alebo prázdny záznam spoločníka</div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Prokurista -->
                    <div v-if="actual_company.prokurista?.length > 0 || updated_company.prokurista?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálny prokurista -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Prokurista — aktuálny</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <div v-for="(prokurista, index) in actual_company.prokurista" :key="'act-prok-' + index"
                                    class="border border-slate-200 rounded-lg p-3 bg-white">
                                    <FormKit type="text" v-model="prokurista.name"
                                        class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                        <!-- Nový prokurista -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Prokurista — zmeny</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <div v-for="(prokurista, index) in updated_company.prokurista" :key="'upd-prok-' + index"
                                    class="border border-teal-200 rounded-lg p-3 bg-white">
                                    <p class="text-xs font-semibold text-slate-500 mb-2">#{{ index }}</p>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div class="flex flex-col" v-if="prokurista.obchodne_meno">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Obchodné meno</label>
                                            <FormKit type="text" v-model="prokurista.obchodne_meno"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Meno</label>
                                            <FormKit type="text" v-model="prokurista.first_name"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Priezvisko</label>
                                            <FormKit type="text" v-model="prokurista.last_name"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Pohlavie</label>
                                            <FormKit type="text" v-model="prokurista.gender"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Rodné číslo</label>
                                            <FormKit type="text" v-model="prokurista.rodne_cislo"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Dátum narodenia</label>
                                            <FormKit type="text" v-model="prokurista.date_of_birth"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Titul pred menom</label>
                                            <FormKit type="text" v-model="prokurista.title_before"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Titul za menom</label>
                                            <FormKit type="text" v-model="prokurista.title_after"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">PSČ</label>
                                            <FormKit type="text" v-model="prokurista.psc"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Mesto</label>
                                            <FormKit type="text" v-model="prokurista.city"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Ulica</label>
                                            <FormKit type="text" v-model="prokurista.street"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Číslo</label>
                                            <FormKit type="text" v-model="prokurista.street_number"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                        <div class="flex flex-col">
                                            <label class="block text-xs font-medium text-teal-700 mb-1">Krajina</label>
                                            <FormKit type="text" v-model="prokurista.country"
                                                class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-xs border-gray-300 rounded-md" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Predmety činnosti -->
                    <div v-if="actual_company.predmet_cinnosti?.length > 0 || updated_company.predmet_cinnosti?.length > 0 || updated_company.removed_subject_business?.length > 0"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Aktuálne predmety -->
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Predmety činnosti — aktuálne</span>
                            </div>
                            <div class="p-4">
                                <div v-for="(predmet, index) in actual_company.predmet_cinnosti" :key="'act-pred-' + index">
                                    <FormKit type="text" v-model="predmet.name"
                                        class="mb-2 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                </div>
                            </div>
                        </div>
                        <!-- Nové/vymazané predmety -->
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Predmety činnosti — zmeny</span>
                            </div>
                            <div class="p-4 flex flex-col gap-3">
                                <div v-if="updated_company.predmet_cinnosti?.length > 0">
                                    <p class="text-xs font-semibold text-green-700 mb-2">Pridávané predmety:</p>
                                    <ul class="list-disc pl-4 text-sm text-slate-700 space-y-1">
                                        <li v-for="predmet in updated_company.predmet_cinnosti" :key="predmet">{{ predmet }}</li>
                                    </ul>
                                </div>
                                <div v-if="updated_company.removed_subject_business?.length > 0">
                                    <p class="text-xs font-semibold text-red-700 mb-2">Vymazávané predmety:</p>
                                    <ul class="list-disc pl-4 text-sm text-red-700 space-y-1">
                                        <li v-for="predmet in updated_company.removed_subject_business" :key="predmet">{{ predmet }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Iné zmeny -->
                    <div v-if="actual_company.ine_zmeny || updated_company.ine_zmeny"
                        class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div class="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-slate-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-slate-500 inline-block"></span>
                                <span class="font-semibold text-slate-700 text-sm">Iné zmeny — aktuálne</span>
                            </div>
                            <div class="p-4">
                                <FormKit type="textarea" v-model="actual_company.ine_zmeny" id="act_ine_zmeny"
                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                            </div>
                        </div>
                        <div class="bg-teal-50 rounded-xl border border-teal-200 overflow-hidden">
                            <div class="px-4 py-2.5 bg-teal-200 flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full bg-teal-600 inline-block"></span>
                                <span class="font-semibold text-teal-800 text-sm">Iné zmeny — nové</span>
                            </div>
                            <div class="p-4">
                                <FormKit type="textarea" v-model="updated_company.ine_zmeny" id="upd_ine_zmeny"
                                    class="focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <!-- old content removed -->
        </div>
    </div>
    <!--Prevod akcii -->
    <div v-if="tab === 3">
        <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="shadow overflow-hidden sm:rounded-md">
                <div class="px-4 py-5 bg-white sm:p-6">
                    <div class="px-4 py-5 bg-white sm:p-6" v-for="(item, index) in sharesTransfers" :key="index">
                        <div class="flex flex-col gap-6 w-full" v-if="item && !Array.isArray(item)">
                            <div class="flex flex-row w-full">
                                <div class="flex flex-col w-full">
                                    <label for="description" class="block text-sm font-bold text-gray-700">Mena</label>
                                    <div>{{ item.currency }}</div>
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="description" class="block text-sm font-bold text-gray-700">Typ
                                        prevodu</label>
                                    <div>{{item.transferType.replace(/([A-Z])/g, (match) => `
                                        ${match}`).replace(/^./, (match) => match.toUpperCase()).trim()}}</div>
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="description" class="block text-sm font-bold text-gray-700">Suma
                                        prevodu</label>
                                    <div>{{ item.amountOfTransfer }}</div>
                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="description" class="block text-sm font-bold text-gray-700">Cena
                                        za prevod</label>
                                    <div>{{ item.priceForTransfer }}</div>
                                </div>
                            </div>
                            <div class="flex flex-row border-t-2">
                                <div class="flex flex-col w-full">
                                    <label for="sharesfrom" class="block text-sm text-gray-700 font-bold py-4">Prevod
                                        od</label>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Meno</label>
                                        <FormKit type="text" v-model="item.sharesFrom.name" id="sharesFrom_name"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Obchodné
                                            meno</label>
                                        <FormKit type="text" v-model="item.sharesFrom.obchodne_meno" id="sharesto_name"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">IČO</label>
                                        <FormKit type="text" v-model="item.sharesFrom.ico" id="sharesFrom_ico"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Titul pred
                                            menom</label>
                                        <FormKit type="text" v-model="item.sharesFrom.title_before" id="sharesFrom_tb"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Titul za
                                            menom</label>
                                        <FormKit type="text" v-model="item.sharesFrom.title_after" id="sharesFrom_ta"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Dátum
                                            narodenia</label>
                                        <FormKit type="text" v-model="item.sharesFrom.date_of_birth" id="sharesFrom_dob"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Rodné
                                            číslo</label>
                                        <FormKit type="text" v-model="item.sharesFrom.rodneCislo" id="sharesFrom_rc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Pohlavie</label>
                                        <FormKit type="text" v-model="item.sharesFrom.pohlavie" id="sharesFrom_pohlavie"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Typ osoby</label>
                                        <FormKit type="text" v-model="item.sharesFrom.typOsoby" id="sharesFrom_type"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">PSČ</label>
                                        <FormKit type="text" v-model="item.sharesFrom.psc" id="sharesFrom_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Mesto</label>
                                        <FormKit type="text" v-model="item.sharesFrom.city" id="sharesFrom_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Ulica</label>
                                        <FormKit type="text" v-model="item.sharesFrom.street" id="sharesFrom_street"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Číslo</label>
                                        <FormKit type="text" v-model="item.sharesFrom.streetNumber" id="sharesFrom_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Štát</label>
                                        <FormKit type="text" v-model="item.sharesFrom.country" id="sharesFrom_country"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>

                                </div>
                                <div class="flex flex-col w-full">
                                    <label for="prijemca"
                                        class="block text-sm font-bold text-gray-700 py-4">Príjemca</label>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Meno</label>
                                        <FormKit type="text" v-model="item.sharesTo.name" id="sharesto_name"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Obchodné
                                            meno</label>
                                        <FormKit type="text" v-model="item.sharesTo.obchodne_meno" id="sharesto_name"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">IČO</label>
                                        <FormKit type="text" v-model="item.sharesTo.ico" id="sharesto_ico"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Titul pred
                                            menom</label>
                                        <FormKit type="text" v-model="item.sharesTo.title_before" id="sharesto_tb"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Titul za
                                            menom</label>
                                        <FormKit type="text" v-model="item.sharesTo.title_after" id="sharesto_ta"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Dátum
                                            narodenia</label>
                                        <FormKit type="text" v-model="item.sharesTo.date_of_birth" id="sharesto_dob"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Rodné
                                            číslo</label>
                                        <FormKit type="text" v-model="item.sharesTo.rodneCislo" id="sharesto_rc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Pohlavie</label>
                                        <FormKit type="text" v-model="item.sharesTo.pohlavie" id="sharesto_pohlavie"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Typ osoby</label>
                                        <FormKit type="text" v-model="item.sharesTo.typOsoby" id="sharesto_type"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">PSČ</label>
                                        <FormKit type="text" v-model="item.sharesTo.psc" id="sharesto_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Mesto</label>
                                        <FormKit type="text" v-model="item.sharesTo.city" id="sharesto_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Ulica</label>
                                        <FormKit type="text" v-model="item.sharesTo.street" id="sharesto_street"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>

                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Číslo</label>
                                        <FormKit type="text" v-model="item.sharesTo.streetNumber" id="sharesto_psc"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">Štát</label>
                                        <FormKit type="text" v-model="item.sharesTo.country" id="sharesto_country"
                                            class="mt-1 focus:ring-teal-500 focus:border-teal-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="py-3 bg-gray-50 text-right">
        <button type="submit"
            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500">
            Uložiť
        </button>
    </div>
</template>
